import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { isLocalhost, sanitizeDifficultyRatings } from "../Config/config";
import useEscapeRoomApi from "../Hooks/useEscapeRoomApi";
import { DifficultyRatings } from "../Types/Game";

/**
 * DifficultyRatingsContext
 * ------------------------
 * Global room difficulty overrides, shared by every event (kept out of
 * GameContext on purpose — see CLAUDE.md). Fetched once signed in and again
 * whenever the window regains focus (the lobby also refreshes on a timer, see
 * RoomsIcons), so machines already open pick up an admin's changes. Cached in
 * localStorage so the lobby shows the right badges straight away on reload;
 * if a fetch fails the last good ratings (or the built-in defaults) are kept.
 */

const STORAGE_KEY = "difficultyRatings";

type DifficultyRatingsContextValue = {
  ratings: DifficultyRatings;
  /** Re-fetch the ratings from the API; keeps the current ones if that fails. */
  refreshRatings: () => Promise<void>;
  /** Persist new overrides for all events. Throws if the save fails. */
  saveRatings: (next: DifficultyRatings) => Promise<void>;
};

export const DifficultyRatingsContext = createContext<DifficultyRatingsContextValue>({
  ratings: {},
  refreshRatings: async () => {},
  saveRatings: async () => {},
});

const readCachedRatings = (): DifficultyRatings => {
  try {
    return sanitizeDifficultyRatings(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}"));
  } catch {
    return {};
  }
};

export const DifficultyRatingsProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const { getDifficultyRatings, saveDifficultyRatings } = useEscapeRoomApi();
  const [ratings, setRatings] = useState<DifficultyRatings>(readCachedRatings);

  // The API needs a token outside localhost, so wait until signed in
  const canFetch = isLocalhost || auth.isAuthenticated;

  // The API functions are recreated every render (they close over the current
  // token) — read the latest through refs so refreshRatings can stay stable.
  const getRatingsRef = useRef(getDifficultyRatings);
  getRatingsRef.current = getDifficultyRatings;
  const canFetchRef = useRef(canFetch);
  canFetchRef.current = canFetch;

  const applyRatings = useCallback((next: DifficultyRatings) => {
    const serialized = JSON.stringify(next);
    // Skip no-op updates so periodic refreshes don't re-render the lobby
    setRatings((prev) => (JSON.stringify(prev) === serialized ? prev : next));
    localStorage.setItem(STORAGE_KEY, serialized);
  }, []);

  const refreshRatings = useCallback(async () => {
    if (!canFetchRef.current) return;
    try {
      applyRatings(sanitizeDifficultyRatings(await getRatingsRef.current()));
    } catch {
      // Keep the cached/default ratings — badges are informational only
    }
  }, [applyRatings]);

  useEffect(() => {
    if (canFetch) refreshRatings();
  }, [canFetch, refreshRatings]);

  // Pick up changes made on other machines when this one is used again
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshRatings();
    };
    window.addEventListener("focus", refreshRatings);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refreshRatings);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshRatings]);

  const saveRatings = async (next: DifficultyRatings) => {
    const clean = sanitizeDifficultyRatings(next);
    await saveDifficultyRatings(clean);
    applyRatings(clean);
  };

  return (
    <DifficultyRatingsContext.Provider value={{ ratings, refreshRatings, saveRatings }}>
      {children}
    </DifficultyRatingsContext.Provider>
  );
};

export const useDifficultyRatings = () => useContext(DifficultyRatingsContext);
